/**
 * API layer — talks to whichever Source we've resolved (LAN pifeeder or
 * remote pifeeder-api). Falls back to bundled sample JSON so the UI is
 * still alive offline / in mock mode.
 */

import runwaysData from "./data/runways.json";
import { $source } from "./discover";
import type { Airport, FlightRecord, Weather } from "./types";

type Hazard = string;

export interface AirmetFeature {
  type: "Feature";
  id: number;
  properties: {
    kind: "AIRMET" | "SIGMET";
    hazard: Hazard;
    severity?: string | null;
    fir?: string | null;
    validFrom?: string;
    validTo?: string;
    altLowFt?: number | null;
    altHighFt?: number | null;
    raw?: string | null;
  };
  geometry: { type: "Polygon"; coordinates: [number, number][][] };
}

export interface AirmetCollection {
  type: "FeatureCollection";
  features: AirmetFeature[];
}

export interface NotamRow {
  id: number;
  notamId: string;
  icao: string;
  severity: "info" | "warn" | "crit" | null;
  notamCode: string | null;
  title: string | null;
  body: string;
  validFrom: string | null;
  validTo: string | null;
  active: boolean;
}

export interface ActiveRunwayEnd {
  runway: string;
  inUse: string;
  headwindKt: number;
  crosswindKt: number;
}
export interface ActiveRunway extends ActiveRunwayEnd {
  /** Every runway end the wind / cross-wind would allow. The first item is
   *  the primary (max head-wind). Older API revisions may not include it. */
  all?: ActiveRunwayEnd[];
}

/** ICAO is uppercase 4 letters; converts iata→icao not handled here yet. */
export function apiBase(): string | null {
  const s = $source.get();
  // `local` mode means we're served same-origin by the Pi's lighttpd, which
  // only owns /data/aircraft.json + /pifeeder/info + a small set of
  // adsb-streamer device-control endpoints. Airport / weather / NOTAM /
  // aircraft-registry queries must still go to the hosted pifeeder-api via
  // the build-time VITE_PIFEEDER_API URL; routing them at window.origin
  // would 404 against the Pi.
  if (s.kind === "api") return s.baseUrl;
  // `local` AND `app` modes both serve the SPA from the Pi origin (or via
  // the Flutter WebView pointed at the Pi). The Pi only owns ADS-B + station
  // endpoints — airport/weather/NOTAM/flights queries must hit the hosted
  // pifeeder-api via the build-time VITE_PIFEEDER_API URL. Without this the
  // app/embedded SPA falls back to bundled sample data and the user sees
  // "mock" weather and flights.
  if (s.kind === "local" || s.kind === "app") {
    const remote = (import.meta.env.VITE_PIFEEDER_API as string | undefined)?.replace(/\/$/, "");
    return remote ?? null;
  }
  return null;
}

/**
 * Public GCS bucket that holds the per-airport static JSONs (regenerated
 * after every METAR/TAF/NOTAM ingest by pifeeder-api's static-gen module).
 * When set, the airport/weather/notam fetchers try this first and fall
 * back to apiBase() Cloud Run if the object is missing — covers ICAOs
 * outside TRACK_AIRPORTS that haven't been pre-rendered.
 *
 * The static-JSON path bypasses Cloud SQL entirely for the hot read path,
 * so DB load stays flat as user count grows (the ingest cron is the only
 * Postgres reader/writer once the SPA is on GCS).
 */
export function staticBase(): string | null {
  return (import.meta.env.VITE_PIFEEDER_STATIC as string | undefined)?.replace(/\/$/, "") ?? null;
}

async function tryJson<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export async function fetchFlights(icao = "RJTT"): Promise<FlightRecord[]> {
  const base = apiBase();
  if (!base) return [];
  const date = new Date().toISOString().slice(0, 10);
  const json = await tryJson<FlightRecord[]>(`${base}/api/airport/${icao}/flights?date=${date}`);
  // No fallback to bundled sample-flights.json: the user explicitly asked
  // for an empty list when the real API is unreachable, since the sample
  // payload (ITM → HND traffic) was confusingly visible for a moment on
  // every reload before discover.ts swapped to the real source.
  return json ?? [];
}

interface AirportFullJson {
  airport: Airport;
  metar?: ServerMetar | null;
  notams?: NotamRow[] | null;
  activeRunway?: ActiveRunway | null;
}

/**
 * Try the static GCS object first, then fall back to the dynamic Cloud
 * Run route. Both shapes are identical (the regenerator mirrors the
 * Express handler) so callers can read either freely.
 */
async function fetchAirportFull(icao: string): Promise<AirportFullJson | null> {
  const sb = staticBase();
  if (sb) {
    const j = await tryJson<AirportFullJson>(`${sb}/airport/${icao}.json`);
    if (j?.airport) return j;
  }
  const base = apiBase();
  if (!base) return null;
  return await tryJson<AirportFullJson>(`${base}/api/airport/${icao}`);
}

export async function fetchAirport(icao = "RJTT"): Promise<Airport | null> {
  const json = await fetchAirportFull(icao);
  if (json?.airport) {
    const ap = json.airport;
    const ar = json.activeRunway;
    return {
      ...ap,
      activeArr: ar?.runway ?? ap.activeArr ?? null,
      activeDep: ar?.runway ?? ap.activeDep ?? null,
      activeEnd: ar?.inUse ?? null,
      activeHeadwindKt: ar?.headwindKt ?? null,
      activeCrosswindKt: ar?.crosswindKt ?? null,
      // Multi-runway list. If the API didn't return `all`, synthesize a
      // single-element list from the primary so downstream code can
      // always iterate uniformly.
      activeEnds: ar?.all ?? (ar ? [ar] : null),
    } as Airport;
  }
  const local = (runwaysData as Record<string, unknown>)[icao] as Airport | undefined;
  if (!local) return null;
  return {
    ...local,
    activeArr: icao === "RJTT" ? "16L/34R" : "32R",
    activeDep: icao === "RJTT" ? "04/22" : "32L",
  };
}

export async function fetchWeather(icao = "RJTT"): Promise<Weather | null> {
  const root = await fetchAirportFull(icao);
  if (root?.metar) return normalizeMetar(root.metar, icao);
  return null; // No mock — show "Loading…" / dashes instead of fake METAR.
}

export async function fetchNotams(icao = "RJTT"): Promise<NotamRow[]> {
  // Static JSON bundles the notams alongside airport+metar+activeRunway,
  // so one fetch (cached by the browser) serves all callers.
  const root = await fetchAirportFull(icao);
  if (root?.notams) return root.notams;
  // If the static path is disabled the bundled payload may not include
  // notams (older API revisions); ask the dedicated endpoint as a last
  // resort.
  const base = apiBase();
  if (!base) return [];
  return (await tryJson<NotamRow[]>(`${base}/api/airport/${icao}/notams`)) ?? [];
}

export async function fetchAirmetSigmet(): Promise<AirmetCollection | null> {
  const sb = staticBase();
  if (sb) {
    const j = await tryJson<AirmetCollection>(`${sb}/airmet-sigmet.json`);
    if (j) return j;
  }
  const base = apiBase();
  if (!base) return null;
  return await tryJson<AirmetCollection>(`${base}/api/weather/airmet-sigmet`);
}

export async function fetchActiveRunway(icao = "RJTT"): Promise<ActiveRunway | null> {
  const root = await fetchAirportFull(icao);
  if (root?.activeRunway) return root.activeRunway;
  const base = apiBase();
  if (!base) return null;
  return await tryJson<ActiveRunway>(`${base}/api/airport/${icao}/active-runway`);
}

/* ─────────── helpers ─────────── */

interface ServerMetar {
  icao: string;
  observed_at: string;
  raw: string;
  temp_c: number | null;
  dew_c: number | null;
  wind_dir: number | null;
  wind_kt: number | null;
  gust_kt: number | null;
  vis_m: number | null;
  qnh_hpa: number | null;
  clouds: Array<{ type?: string; altFt?: number }> | null;
  weather: string[] | null;
  flight_cat: string | null;
}

function normalizeMetar(m: ServerMetar, icao: string): Weather {
  const tempC = m.temp_c ?? 0;
  const dewC = m.dew_c ?? tempC;
  const humidity = relativeHumidity(tempC, dewC);
  const cloudsTxt =
    m.clouds
      ?.filter((c) => c?.type)
      .map((c) => `${c.type} ${Math.round((c.altFt ?? 0) / 100) * 100}`)
      .join(" · ") ?? "";
  return {
    icao,
    iata: icao === "RJTT" ? "HND" : icao === "RJCC" ? "CTS" : icao.slice(-3),
    tempC,
    feelsC: tempC,
    condition: m.flight_cat ?? "—",
    windDeg: m.wind_dir ?? 0,
    windKt: m.wind_kt ?? 0,
    gustKt: m.gust_kt ?? undefined,
    // METAR visibility: < 10 km is the interesting range (anything above is
    // effectively "unlimited" for general aviation). Keep one decimal under
    // 10 km so 9.6 km doesn't round to "10 km", and floor anything ≥10 km
    // back to 10. Null means we didn't get a reading at all.
    visKm: m.vis_m == null ? null : m.vis_m >= 10000 ? 10 : Math.round(m.vis_m / 100) / 10,
    clouds: cloudsTxt,
    humidity,
    dewC,
    qnh: m.qnh_hpa ?? 0,
    metar: m.raw,
    observedAt: new Date(m.observed_at).toISOString().slice(11, 16) + "Z",
  };
}

function relativeHumidity(tempC: number, dewC: number): number {
  // Magnus approximation
  const a = 17.625;
  const b = 243.04;
  const rh = 100 * (Math.exp((a * dewC) / (b + dewC)) / Math.exp((a * tempC) / (b + tempC)));
  return Math.round(Math.max(0, Math.min(100, rh)));
}
